import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import shellStyles from '../components/shell/shell.module.css';
import ui from '../styles/ui.module.css';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  sending: 'Enviando…',
  sent: 'Enviada',
  failed: 'Falhou',
};

const STATUS_PILL: Record<string, string> = {
  draft: 'pillMuted',
  sending: 'pillWarn',
  sent: 'pillPos',
  failed: 'pillDanger',
};

function when(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/** P18 · Campanhas — broadcast pro grupo do Telegram (camada L5). */
export function Campanhas() {
  const [creating, setCreating] = useState(false);
  const campaigns = useQuery({ queryKey: ['campaigns'], queryFn: () => api.campaigns() });

  return (
    <>
      <div className={shellStyles.head}>
        <h1>Campanhas</h1>
        <p>Mande um aviso pra todo mundo que está no seu grupo do Telegram.</p>
      </div>

      <div className={ui.actions}>
        <button className={`${ui.btn} ${ui.btnPrimary}`} onClick={() => setCreating((v) => !v)}>
          {creating ? 'Fechar' : '+ Nova campanha'}
        </button>
      </div>

      {creating ? (
        <div className={ui.card} style={{ padding: '18px 20px' }}>
          <NewCampaignForm onCreated={() => setCreating(false)} />
        </div>
      ) : null}

      <section>
        <div className={ui.secH}>
          <h2>Histórico</h2>
        </div>
        <div className={ui.card}>
          {!campaigns.data?.length ? (
            <p className={ui.empty}>Nenhuma campanha ainda.</p>
          ) : (
            <div className={ui.tblScroll}>
              <table className={ui.tbl}>
                <thead>
                  <tr>
                    <th>Mensagem</th>
                    <th>Público</th>
                    <th className="r">Entregues</th>
                    <th className="r">Falhas</th>
                    <th>Status</th>
                    <th>Quando</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.data.map((c) => (
                    <tr key={c.id}>
                      <td className={ui.muted}>{c.text}</td>
                      <td className={ui.muted}>{c.audience === 'all' ? 'Todos' : 'Ativos'}</td>
                      <td className="r">
                        {c.deliveredCount}/{c.recipientCount}
                      </td>
                      <td className="r">{c.failedCount || '—'}</td>
                      <td>
                        <span className={`${ui.pill} ${ui[STATUS_PILL[c.status] ?? 'pillMuted']}`}>
                          {STATUS_LABEL[c.status] ?? c.status}
                        </span>
                      </td>
                      <td className={ui.faint}>{when(c.sentAt ?? c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function NewCampaignForm({ onCreated }: { onCreated: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [audience, setAudience] = useState('active');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.createCampaign({ text, link: link || undefined, audience }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      onCreated();
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Não foi possível enviar'),
  });

  return (
    <form
      className={ui.form}
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        create.mutate();
      }}
    >
      <label className={ui.field}>
        Mensagem
        <textarea rows={4} required value={text} onChange={(e) => setText(e.target.value)} />
      </label>
      <label className={ui.field}>
        Link (opcional)
        <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" />
      </label>
      <label className={ui.field}>
        Para quem
        <select value={audience} onChange={(e) => setAudience(e.target.value)}>
          <option value="active">Assinantes/clientes ativos</option>
          <option value="all">Todos que já entraram no grupo</option>
        </select>
      </label>
      {error ? <p className={ui.error}>{error}</p> : null}
      <div className={ui.actions}>
        <button className={`${ui.btn} ${ui.btnPrimary}`} type="submit" disabled={create.isPending}>
          {create.isPending ? 'Enviando…' : 'Enviar campanha'}
        </button>
      </div>
    </form>
  );
}
